import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { track } from '../lib/analytics';
import { cn } from '../lib/utils';

const SITE_ORIGIN = 'https://signal.app';

export function WaitlistConfirmationPage() {
  const { t } = useTranslation('waitlist');
  const { t: tc } = useTranslation('common');
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    track('page_viewed', { page: 'waitlist_confirmed' });
  }, []);

  const referralUrl = token ? `${SITE_ORIGIN}?ref=${encodeURIComponent(token)}` : null;

  const handleCopy = async () => {
    if (!referralUrl) return;
    track('referral_link_copied');
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = referralUrl;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const twitterShareUrl = referralUrl
    ? `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        t('confirmation.shareText', { url: referralUrl })
      )}`
    : null;

  const linkedInShareUrl = referralUrl
    ? `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralUrl)}`
    : null;

  return (
    <div className="mx-auto my-20 max-w-[560px] px-4 text-center font-sans text-brand-text">
      <div className="mb-4 flex justify-end">
        <LanguageSwitcher />
      </div>
      <h1 className="mb-4 text-[clamp(28px,5vw,40px)] font-extrabold tracking-tight">
        {t('confirmation.title')}
      </h1>

      <p className="mb-3 text-[17px] leading-[1.6] text-[#555]">
        {t('confirmation.subtitle')}
      </p>

      <p className="mb-8 text-[15px] font-semibold text-brand-text">
        {t('confirmation.referralNote')}
      </p>

      {referralUrl ? (
        <>
          {/* Referral link display */}
          <div className="mb-6 flex items-center gap-2 rounded-lg border border-[#e0e0e0] bg-[#f5f5f5] px-3 py-2.5">
            <input
              type="text"
              readOnly
              value={referralUrl}
              className="min-w-0 flex-1 border-0 bg-transparent text-sm text-[#333] outline-none"
              onClick={(e) => (e.target as HTMLInputElement).select()}
            />
            <button
              type="button"
              onClick={handleCopy}
              className={cn(
                'cursor-pointer whitespace-nowrap rounded-md px-4 py-2 text-sm font-semibold text-white transition-colors duration-200',
                copied ? 'bg-accent-green' : 'bg-accent-blue'
              )}
            >
              {copied ? tc('copied') : tc('copy')}
            </button>
          </div>

          {/* Share buttons */}
          <div className="mb-8 flex justify-center gap-3">
            {twitterShareUrl && (
              <a
                href={twitterShareUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track('referral_share_clicked', { platform: 'twitter' })}
                className="inline-flex items-center gap-2 rounded-md bg-black px-5 py-2.5 text-sm font-semibold text-white no-underline"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
                {t('confirmation.shareOnX')}
              </a>
            )}
            {linkedInShareUrl && (
              <a
                href={linkedInShareUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track('referral_share_clicked', { platform: 'linkedin' })}
                className="inline-flex items-center gap-2 rounded-md bg-accent-linkedin px-5 py-2.5 text-sm font-semibold text-white no-underline"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
                {t('confirmation.shareOnLinkedIn')}
              </a>
            )}
          </div>
        </>
      ) : (
        <p className="mb-8 text-sm text-[#999]">
          {t('confirmation.noReferral')}
        </p>
      )}

      <Link to="/" className="text-[15px] text-accent-blue">
        {t('confirmation.backToHome')}
      </Link>
    </div>
  );
}
